import type {Collaboration} from 'collaborator-components/useCollaborationManagementState';
import ModelTypeConstants from 'constants/ModelTypeConstants';

/** The kinds of parties a skill can be shared with, bucketed for analytics. */
export type ShareKind = 'user' | 'organization' | 'other';

interface AnalyticsParty {
  id: string;
  kind: ShareKind;
}

export interface SharingAnalyticsMetrics {
  sharingAddedUserCount: number;
  sharingAddedOrganizationCount: number;
  sharingAddedOtherCount: number;
  sharingRemovedUserCount: number;
  sharingRemovedOrganizationCount: number;
  sharingRemovedOtherCount: number;
  totalSharedWithUserCount: number;
  totalSharedWithOrganizationCount: number;
  totalSharedWithOtherCount: number;
}

/** Buckets a party's ModelType (e.g. an owner's `type`) into the analytics {@link ShareKind}. */
export function getPartyTypeAnalyticsValue(type: string | null | undefined): ShareKind {
  if (type === ModelTypeConstants.USER) {
    return 'user';
  }
  if (type === ModelTypeConstants.ORGANIZATION) {
    return 'organization';
  }
  return 'other';
}

/** Converts a collaboration into the id/kind shape used by {@link buildSharingAnalyticsMetrics}. */
export function collaborationToAnalyticsParty(collaboration: Collaboration): AnalyticsParty {
  const typeName = collaboration.party.__typename;
  const kind: ShareKind =
    typeName === 'UserNode' ? 'user' : typeName === 'OrganizationNode' ? 'organization' : 'other';
  return {id: collaboration.party.id, kind};
}

/** Counts, for a single kind, how many party ids were added, removed, and remain shared. */
function diffForKind(
  kind: ShareKind,
  existingParties: Array<AnalyticsParty>,
  nextParties: Array<AnalyticsParty>
): {added: number; removed: number; total: number} {
  const existingIds = new Set(existingParties.filter((p) => p.kind === kind).map((p) => p.id));
  const nextIds = new Set(nextParties.filter((p) => p.kind === kind).map((p) => p.id));
  return {
    added: [...nextIds].filter((id) => !existingIds.has(id)).length,
    removed: [...existingIds].filter((id) => !nextIds.has(id)).length,
    total: nextIds.size,
  };
}

/**
 * Compares the parties a skill was previously shared with against the parties it will be shared with,
 * producing per-kind added/removed/total counts for analytics. A null `nextParties` means the sharing
 * set was left unchanged.
 */
export function buildSharingAnalyticsMetrics(
  existingParties: Array<AnalyticsParty>,
  nextParties: Array<AnalyticsParty> | null
): SharingAnalyticsMetrics {
  const normalizedNextParties = nextParties ?? existingParties;
  const user = diffForKind('user', existingParties, normalizedNextParties);
  const organization = diffForKind('organization', existingParties, normalizedNextParties);
  const other = diffForKind('other', existingParties, normalizedNextParties);

  return {
    sharingAddedUserCount: user.added,
    sharingAddedOrganizationCount: organization.added,
    sharingAddedOtherCount: other.added,
    sharingRemovedUserCount: user.removed,
    sharingRemovedOrganizationCount: organization.removed,
    sharingRemovedOtherCount: other.removed,
    totalSharedWithUserCount: user.total,
    totalSharedWithOrganizationCount: organization.total,
    totalSharedWithOtherCount: other.total,
  };
}
